import React, { useEffect, useState } from "react";
import { Button, Form } from "antd";
import { useTranslation } from "react-i18next";
import DeviceReferenceId from "./DeviceReferenceId";
import DeviceMacAddress from "./DeviceMacAddress";
import DeviceType from "./DeviceType";
import DeviceBrand from "./DeviceBrand";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  createDevice,
  createDeviceClear,
  createDeviceRequest,
  getDevice,
  getDeviceBrandListForSelect,
  getDeviceBrandListForSelectRequest,
  getDeviceListRequest,
  getMyOrganization,
  updateDevice,
  updateDeviceClear,
  updateDeviceRequest,
  useDynamicSelector,
} from "@src/redux";
import toast from "react-hot-toast";

interface DeviceFormProps {
  handleCancel: () => void;
  mutationType: "edit" | "add";
  deviceId?: string;
}

interface DeviceType {
  id: string;
  deviceType: string;
  name: string;
}

const DeviceForm: React.FC<DeviceFormProps> = ({
  handleCancel,
  mutationType,
  deviceId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const { result: getMyOrg } = useSelector(getMyOrganization);
  const { result: getDeviceDetail, loading: getDeviceDetailLoading } =
    useSelector(getDevice);
  const { result: updateDeviceDetails, loading: updateDeviceLoading } =
    useSelector(updateDevice);
  const { result: createDeviceDetail, loading: createDeviceLoading } =
    useSelector(createDevice);
  const deviceFilter = useDynamicSelector("device_filter");

  useEffect(() => {
    if (updateDeviceDetails?.status === "success") {
      handleCancel();
      dispatch(
        getDeviceListRequest({
          ...deviceFilter,
        })
      );
    }
  }, [updateDeviceDetails?.status]);

  useEffect(() => {
    if (createDeviceDetail?.status === "success") {
      handleCancel();
      dispatch(
        getDeviceListRequest({
          ...deviceFilter,
        })
      );
    }
  }, [createDeviceDetail?.status]);
  const onFinish = (val: any) => {
    mutationType === "add"
      ? dispatch(
          createDeviceRequest({
            data: {
              macAddress: val?.macAddress,
              type: val?.deviceType,
              brandId: val?.deviceBrand,
              referenceId: val?.referenceId,
              organizationId: getMyOrg?.id,
            },
          })
        )
      : dispatch(
          updateDeviceRequest({
            id: deviceId || "",
            data: {
              macAddress: val?.macAddress,
              type: val?.deviceType,
              brandId: val?.deviceBrand,
              referenceId: val?.referenceId,
              organizationId: getMyOrg?.id,
            },
          })
        );
  };

  const onFieldsChange = (changedValues: any, allValues: any) => {
    if (changedValues.deviceType) {
      form.setFieldValue("deviceBrand", null);
    }
  };

  useEffect(() => {
    if (mutationType === "edit" && deviceId && getDeviceDetail) {
      form.setFieldsValue({
        macAddress: getDeviceDetail.macAddress,
        deviceType: getDeviceDetail.type,
        deviceBrand: getDeviceDetail?.brand?.id,
        referenceId: getDeviceDetail.referenceId,
      });
    }
  }, [mutationType, deviceId, getDeviceDetail, form]);

  if (
    mutationType === "edit" &&
    deviceId &&
    getDeviceDetailLoading &&
    !getDeviceDetail
  ) {
    return (
      <div className="inline-loading-wrap">
        <div className="inline-loading-spinner" />
        <div className="inline-loading-text">
          {t("loading_device_details") || "Loading device details..."}
        </div>
      </div>
    );
  }

  return (
    <Form
      layout="vertical"
      className="device-form"
      onFinish={onFinish}
      onValuesChange={onFieldsChange}
      form={form}
    >
      <DeviceReferenceId />
      <DeviceMacAddress />
      <DeviceType />
      <DeviceBrand form={form} />

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Form.Item>
          <Button
            style={{ marginRight: "10px" }}
            type="default"
            onClick={handleCancel}
            danger
          >
            {t("close")}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={updateDeviceLoading || createDeviceLoading}
          >
            {mutationType === "edit" ? t("save") : t("add")}
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default DeviceForm;
